import { crashMessage, diagnostics } from './crash';
import { telemetry } from '@core/telemetry';

/**
 * Последний рубеж: белый экран обязан заговорить.
 *
 * 15.09.2026 пришла жалоба «не могут зайти, белый экран». Журнал сервера снял с сервера
 * подозрения за минуту — 117 ответов за сутки, все 200 и 304, ни одного 4xx, — а дальше
 * выяснять было нечем. Приложение умеет показать и отказ хранилища, и затянувшееся
 * открытие, но исключение при отрисовке React 18 обрабатывает единственным способом:
 * снимает дерево целиком. Остаётся пустой `<div id="root">` — белизна без единого слова,
 * и ни устройство, ни сервер об этом не скажут ничего.
 *
 * Здесь не почин исправления причины, а способ её **узнать**: экран называет ошибку,
 * юрист присылает снимок. По правилу CLAUDE.md §8 после неподтвердившейся правки идёт
 * измерение, а не вторая правка вслепую.
 *
 * **Никуда не отправляется.** Решение владельца 15.09.2026: текст ошибки живёт только
 * на экране. В нём может оказаться кусок названия дела, а состав собираемых данных
 * согласуется отдельно (CLAUDE.md §7).
 */

let painted = false;

/**
 * Ставит обработчики на то, что не ловится ничем другим: падение при загрузке модулей,
 * необработанный `reject` и ошибку отрисовки, о которой React сообщает через `reportError`.
 *
 * Вызывается **первой строкой** приложения — раньше React, раньше темы, раньше базы.
 * Смысл рубежа в том, чтобы он стоял до всего, что может упасть.
 */
export function installLastResort(): void {
  window.addEventListener('error', (event) => schedule(event.error ?? event.message));
  window.addEventListener('unhandledrejection', (event) => schedule(event.reason));
}

/**
 * Рисует экран падения, **только если экран действительно пуст**.
 *
 * Это и есть всё правило. Ошибка в фоне — в отправке статистики, в предзагрузке — работе
 * не мешает, и перебивать ею живой экран нельзя: юрист потеряет то, что набирал.
 * Пустой `#root` — единственный случай, когда показывать уже нечего.
 *
 * Проверяется не сразу, а через тик и ещё раз через секунду: React сообщает об ошибке
 * до того, как снял дерево, и мгновенная проверка застала бы экран ещё целым.
 */
function schedule(reason: unknown): void {
  const check = () => paintIfBlank(reason);

  setTimeout(check, 0);
  setTimeout(check, 1000);
}

function paintIfBlank(reason: unknown): void {
  const root = document.getElementById('root');
  if (painted || !root || root.childElementCount > 0) return;

  painted = true;
  /*
   * Считается ровно здесь — там же, где принято решение рисовать.
   *
   * Не в обработчике события: `schedule` зовётся на каждую ошибку, в том числе фоновую,
   * которая экран не трогает. Падением считается то, после чего смотреть стало не на что.
   * Флаг `painted` держит счёт на одном: второй экран не рисуется, и второе число
   * не пишется.
   */
  telemetry.record('appCrashed');
  root.append(screen(crashMessage(reason)));
}

/**
 * Собирается узлами, а не разметкой строкой.
 *
 * `innerHTML` с текстом ошибки внутри — это подстановка чужого текста в разметку:
 * в сообщение попадает и то, что пришло из документа, и то, что ввёл человек.
 * `textContent` не разбирает ничего.
 */
function screen(message: string): HTMLElement {
  const box = document.createElement('div');
  box.className = 'crash';
  box.setAttribute('role', 'alert');

  box.append(
    element('h1', 'crash__title', 'Приложение не открылось'),
    element(
      'p',
      'crash__text',
      'Дела и документы на месте — они лежат на устройстве, и эта ошибка их не трогает.',
    ),
    element('p', 'crash__reason', message),
    element('p', 'crash__facts', diagnostics(__APP_VERSION__)),
    element(
      'p',
      'crash__text',
      'Покажите этот экран разработчику: по нему видно причину. Снимок делается кнопками громкости и боковой.',
    ),
  );

  const again = document.createElement('button');
  again.type = 'button';
  again.className = 'button crash__button';
  again.textContent = 'Попробовать снова';
  again.addEventListener('click', () => window.location.reload());
  box.append(again);

  return box;
}

function element(tag: string, className: string, text: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}
